import {
	formatAspect,
	formatDateTime,
	formatNumber,
} from '@sohanscript/lib/utils/format';
import { useEffect, useState } from 'react';

export type ImageInspectorHints = {
	filename?: string | null;
	size?: number | null;
	mimeType?: string | null;
	width?: number | null;
	height?: number | null;
};

export type ImageInspectorExif = {
	aperture: string | null;
	shutter: string | null;
	iso: string | null;
	focalLength: string | null;
	camera: string | null;
	lens: string | null;
	capturedAt: string | null;
	metering: string | null;
};

export type ImageInspectorGps = {
	latitude: number;
	longitude: number;
	dms: string;
	decimal: string;
	osmUrl: string;
};

export type ImageInspectorData = {
	filename: string;
	format: string | null;
	mimeType: string | null;
	size: number | null;
	width: number | null;
	height: number | null;
	aspectLabel: string | null;
	aspectDecimal: string | null;
	palette: string[];
	exif: ImageInspectorExif | null;
	gps: ImageInspectorGps | null;
	colorSpace: string | null;
	isLimited: boolean;
};

export type ImageInspectorResult = {
	data: ImageInspectorData;
	isLoading: boolean;
};

const MIME_FORMATS: Record<string, string> = {
	'image/avif': 'AVIF',
	'image/bmp': 'BMP',
	'image/gif': 'GIF',
	'image/heic': 'HEIC',
	'image/heif': 'HEIF',
	'image/jpeg': 'JPG',
	'image/jpg': 'JPG',
	'image/png': 'PNG',
	'image/svg+xml': 'SVG',
	'image/tiff': 'TIFF',
	'image/vnd.microsoft.icon': 'ICO',
	'image/webp': 'WEBP',
	'image/x-icon': 'ICO',
	'image/x-png': 'PNG',
};

const EXTENSION_FORMATS: Record<string, string> = {
	avif: 'AVIF',
	bmp: 'BMP',
	gif: 'GIF',
	heic: 'HEIC',
	heif: 'HEIF',
	ico: 'ICO',
	jpeg: 'JPG',
	jpg: 'JPG',
	png: 'PNG',
	svg: 'SVG',
	tif: 'TIFF',
	tiff: 'TIFF',
	webp: 'WEBP',
};

const METERING_MODES: Record<number, string> = {
	0: 'Unknown',
	1: 'Average',
	2: 'Center-weighted average',
	3: 'Spot',
	4: 'Multi-spot',
	5: 'Pattern',
	6: 'Partial',
	255: 'Other',
};

const METERING_LABELS: Record<string, string> = {
	CenterWeightedAverage: 'Center-weighted average',
	MultiSpot: 'Multi-spot',
};

function safeDecode(value: string) {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
}

function filenameFromUrl(url: string) {
	try {
		const pathname = new URL(url, 'http://localhost').pathname;
		const segment = pathname.split('/').filter(Boolean).at(-1);
		return segment ? safeDecode(segment) : 'image';
	} catch {
		const segment = url.split('?')[0]?.split('/').filter(Boolean).at(-1);
		return segment ? safeDecode(segment) : 'image';
	}
}

function extensionFromFilename(filename: string) {
	const clean = filename.split('?')[0] ?? filename;
	const extension = clean.split('.').at(-1)?.toLowerCase();
	return extension && extension !== clean ? extension : null;
}

function formatFromFilename(filename: string) {
	const extension = extensionFromFilename(filename);
	return extension
		? (EXTENSION_FORMATS[extension] ?? extension.toUpperCase())
		: null;
}

function formatFromMime(mimeType: string | null) {
	if (!mimeType) return null;
	return MIME_FORMATS[mimeType.toLowerCase()] ?? null;
}

function createInitialData(
	imageUrl: string,
	hints: Required<
		Pick<
			ImageInspectorHints,
			'filename' | 'size' | 'mimeType' | 'width' | 'height'
		>
	>,
): ImageInspectorData {
	const filename = hints.filename?.trim() || filenameFromUrl(imageUrl);
	const mimeType = hints.mimeType ?? null;
	const aspect = formatAspect(hints.width, hints.height);

	return {
		filename,
		format: formatFromMime(mimeType) ?? formatFromFilename(filename),
		mimeType,
		size: hints.size ?? null,
		width: hints.width ?? null,
		height: hints.height ?? null,
		aspectLabel: aspect?.label ?? null,
		aspectDecimal: aspect?.decimal ?? null,
		palette: [],
		exif: null,
		gps: null,
		colorSpace: null,
		isLimited: false,
	};
}

function asNumber(value: unknown): number | null {
	if (typeof value === 'number' && Number.isFinite(value)) return value;
	if (Array.isArray(value)) {
		for (const entry of value) {
			const parsed = asNumber(entry);
			if (parsed != null) return parsed;
		}
	}
	if (typeof value === 'string') {
		const parsed = Number(value);
		if (Number.isFinite(parsed)) return parsed;
	}
	return null;
}

function asString(value: unknown): string | null {
	if (typeof value === 'string' && value.trim()) return value.trim();
	if (typeof value === 'number' && Number.isFinite(value)) return String(value);
	return null;
}

function trimNumber(value: number, precision = 2) {
	return formatNumber(value, { maximumFractionDigits: precision });
}

function formatAperture(value: number | null) {
	return value == null ? null : `f/${trimNumber(value)}`;
}

function formatShutter(value: number | null) {
	if (value == null || value <= 0) return null;
	if (value >= 1) return `${trimNumber(value, 3)}s`;
	return `1/${formatNumber(Math.round(1 / value), { useGrouping: false })}s`;
}

function formatFocalLength(value: number | null) {
	return value == null ? null : `${trimNumber(value)} mm`;
}

function formatCamera(raw: Record<string, unknown>) {
	const make = asString(raw.Make);
	const model = asString(raw.Model);
	if (!make) return model;
	if (!model) return make;
	return model.toLowerCase().startsWith(make.toLowerCase())
		? model
		: `${make} ${model}`;
}

function formatCapturedAt(value: unknown) {
	if (value instanceof Date && !Number.isNaN(value.getTime())) {
		return formatDateTime(value);
	}
	return asString(value);
}

function formatMetering(value: unknown) {
	const mode = asNumber(value);
	if (mode != null) return METERING_MODES[mode] ?? `Mode ${mode}`;
	const label = asString(value);
	if (!label) return null;
	return METERING_LABELS[label] ?? label.replace(/([a-z])([A-Z])/g, '$1 $2');
}

function formatColorSpace(value: unknown) {
	const colorSpace = asNumber(value);
	if (colorSpace === 1) return 'sRGB';
	if (colorSpace === 2) return 'Adobe RGB';
	if (colorSpace === 65535) return 'Uncalibrated';
	return asString(value);
}

function formatDms(latitude: number, longitude: number) {
	const format = (value: number, positive: string, negative: string) => {
		const absolute = Math.abs(value);
		const degrees = Math.floor(absolute);
		const minutesFloat = (absolute - degrees) * 60;
		const minutes = Math.floor(minutesFloat);
		const seconds = formatNumber((minutesFloat - minutes) * 60, {
			minimumFractionDigits: 1,
			maximumFractionDigits: 1,
		});
		return `${formatNumber(degrees, { useGrouping: false })}° ${formatNumber(minutes, { useGrouping: false })}' ${seconds}" ${value >= 0 ? positive : negative}`;
	};

	return `${format(latitude, 'N', 'S')} ${format(longitude, 'E', 'W')}`;
}

function loadImage(src: string, crossOrigin = false) {
	return new Promise<HTMLImageElement>((resolve, reject) => {
		const image = new Image();
		if (crossOrigin) image.crossOrigin = 'anonymous';
		image.decoding = 'async';
		image.onload = () => resolve(image);
		image.onerror = () => reject(new Error('Image could not be loaded'));
		image.src = src;
	});
}

type Rgb = [number, number, number];

function luminance([red, green, blue]: Rgb) {
	return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

function distance(left: Rgb, right: Rgb) {
	const red = left[0] - right[0];
	const green = left[1] - right[1];
	const blue = left[2] - right[2];
	return red * red + green * green + blue * blue;
}

function toHex([red, green, blue]: Rgb) {
	return `#${[red, green, blue]
		.map((channel) => Math.round(channel).toString(16).padStart(2, '0'))
		.join('')
		.toUpperCase()}`;
}

function nearestCentroid(pixel: Rgb, centroids: Rgb[]) {
	let nearestIndex = 0;
	let nearestDistance = Number.POSITIVE_INFINITY;
	for (let index = 0; index < centroids.length; index++) {
		const nextDistance = distance(pixel, centroids[index]);
		if (nextDistance < nearestDistance) {
			nearestDistance = nextDistance;
			nearestIndex = index;
		}
	}
	return nearestIndex;
}

function kMeansPalette(data: Uint8ClampedArray, count: number) {
	const pixels: Rgb[] = [];
	for (let index = 0; index < data.length; index += 4) {
		if (data[index + 3] < 128) continue;
		pixels.push([data[index], data[index + 1], data[index + 2]]);
	}
	if (!pixels.length) return [];

	const sampleStep = Math.max(1, Math.floor(pixels.length / 2500));
	const sample = pixels.filter((_, index) => index % sampleStep === 0);
	const sorted = [...sample].sort(
		(left, right) => luminance(left) - luminance(right),
	);
	const clusterCount = Math.min(6, Math.max(1, count + 1));
	let centroids: Rgb[] = Array.from(
		{ length: clusterCount },
		(_, index): Rgb => {
			const position = Math.floor(
				((index + 0.5) * sorted.length) / clusterCount,
			);
			return sorted[Math.min(position, sorted.length - 1)] ?? [0, 0, 0];
		},
	);

	for (let iteration = 0; iteration < 8; iteration++) {
		const totals = Array.from({ length: clusterCount }, () => ({
			red: 0,
			green: 0,
			blue: 0,
			count: 0,
		}));

		for (const pixel of sample) {
			const total = totals[nearestCentroid(pixel, centroids)];
			total.red += pixel[0];
			total.green += pixel[1];
			total.blue += pixel[2];
			total.count += 1;
		}

		centroids = centroids.map((centroid, index) => {
			const total = totals[index];
			if (!total.count) return centroid;
			return [
				total.red / total.count,
				total.green / total.count,
				total.blue / total.count,
			];
		});
	}

	const counts = new Array<number>(clusterCount).fill(0);
	for (const pixel of sample) {
		counts[nearestCentroid(pixel, centroids)] += 1;
	}
	const ranked = centroids
		.map((centroid, index) => ({ centroid, count: counts[index] }))
		.sort((left, right) => right.count - left.count);

	const seen = new Set<string>();
	const colors: string[] = [];
	for (const { centroid } of ranked) {
		const hex = toHex(centroid);
		if (seen.has(hex)) continue;
		seen.add(hex);
		colors.push(hex);
		if (colors.length === count) break;
	}
	return colors;
}

function paletteFromImage(image: HTMLImageElement) {
	const canvas = document.createElement('canvas');
	canvas.width = 100;
	canvas.height = 100;
	const context = canvas.getContext('2d', { willReadFrequently: true });
	if (!context) return [];
	context.drawImage(image, 0, 0, canvas.width, canvas.height);
	return kMeansPalette(
		context.getImageData(0, 0, canvas.width, canvas.height).data,
		5,
	);
}

async function paletteFromSource(src: string, crossOrigin: boolean) {
	const image = await loadImage(src, crossOrigin);
	return paletteFromImage(image);
}

async function paletteFromBlob(blob: Blob) {
	const objectUrl = URL.createObjectURL(blob);
	try {
		return await paletteFromSource(objectUrl, false);
	} finally {
		URL.revokeObjectURL(objectUrl);
	}
}

type ParsedExif = {
	exif: ImageInspectorExif | null;
	gps: ImageInspectorGps | null;
	colorSpace: string | null;
};

const EMPTY_EXIF: ParsedExif = {
	exif: null,
	gps: null,
	colorSpace: null,
};

async function parseExif(blob: Blob): Promise<ParsedExif> {
	try {
		const { parse } = await import('exifr');
		const raw = (await parse(blob, {
			tiff: true,
			exif: true,
			gps: true,
			icc: true,
			mergeOutput: true,
			translateKeys: true,
			translateValues: true,
			reviveValues: true,
		})) as Record<string, unknown> | null;
		if (!raw) return EMPTY_EXIF;

		const exif: ImageInspectorExif = {
			aperture: formatAperture(asNumber(raw.FNumber)),
			shutter: formatShutter(asNumber(raw.ExposureTime)),
			iso: (() => {
				const iso =
					asNumber(raw.ISO) ??
					asNumber(raw.ISOSpeed) ??
					asNumber(raw.PhotographicSensitivity);
				return iso == null
					? null
					: formatNumber(Math.round(iso), { useGrouping: false });
			})(),
			focalLength: formatFocalLength(asNumber(raw.FocalLength)),
			camera: formatCamera(raw),
			lens:
				asString(raw.LensModel) ?? asString(raw.Lens) ?? asString(raw.LensInfo),
			capturedAt: formatCapturedAt(raw.DateTimeOriginal ?? raw.CreateDate),
			metering: formatMetering(raw.MeteringMode),
		};
		const hasExif = Object.values(exif).some(Boolean);

		const latitude = asNumber(raw.latitude);
		const longitude = asNumber(raw.longitude);
		const gps =
			latitude != null && longitude != null
				? {
						latitude,
						longitude,
						dms: formatDms(latitude, longitude),
						decimal: `${formatNumber(latitude, { minimumFractionDigits: 5, maximumFractionDigits: 5 })}, ${formatNumber(longitude, { minimumFractionDigits: 5, maximumFractionDigits: 5 })}`,
						osmUrl: `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}`,
					}
				: null;

		const colorSpace =
			asString(raw.ProfileDescription) ??
			formatColorSpace(raw.ColorSpace) ??
			asString(raw.ColorSpaceData);

		return { exif: hasExif ? exif : null, gps, colorSpace };
	} catch {
		return EMPTY_EXIF;
	}
}

export function useImageInspector(
	imageUrl: string,
	hints: ImageInspectorHints = {},
): ImageInspectorResult {
	const filename = hints.filename ?? null;
	const size = hints.size ?? null;
	const mimeType = hints.mimeType ?? null;
	const width = hints.width ?? null;
	const height = hints.height ?? null;
	const [data, setData] = useState<ImageInspectorData>(() =>
		createInitialData(imageUrl, { filename, size, mimeType, width, height }),
	);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (!imageUrl) {
			setData(
				createInitialData('', { filename, size, mimeType, width, height }),
			);
			setIsLoading(false);
			return;
		}

		let isCancelled = false;
		const controller = new AbortController();
		setData(
			createInitialData(imageUrl, { filename, size, mimeType, width, height }),
		);
		setIsLoading(true);

		const run = async () => {
			let intrinsicWidth = width;
			let intrinsicHeight = height;
			try {
				const image = await loadImage(imageUrl);
				intrinsicWidth = image.naturalWidth || width;
				intrinsicHeight = image.naturalHeight || height;
			} catch {
				// NOTE: Intrinsic dimensions are best-effort; hints remain usable.
			}
			if (isCancelled) return;
			const intrinsicAspect = formatAspect(intrinsicWidth, intrinsicHeight);
			setData((current) => ({
				...current,
				width: intrinsicWidth,
				height: intrinsicHeight,
				aspectLabel: intrinsicAspect?.label ?? current.aspectLabel,
				aspectDecimal: intrinsicAspect?.decimal ?? current.aspectDecimal,
			}));

			let blob: Blob | null = null;
			try {
				const response = await fetch(imageUrl, {
					signal: controller.signal,
					credentials: 'omit',
					mode: 'cors',
				});
				if (!response.ok) throw new Error('Image request failed');
				blob = await response.blob();
			} catch {
				if (controller.signal.aborted) return;
			}
			if (isCancelled) return;

			if (blob) {
				const resolvedMimeType = blob.type || mimeType;
				const resolvedFormat =
					formatFromMime(resolvedMimeType) ??
					formatFromFilename(filename ?? filenameFromUrl(imageUrl));
				setData((current) => ({
					...current,
					size: blob.size,
					mimeType: resolvedMimeType,
					format: resolvedFormat ?? current.format,
				}));

				const [palette, parsed] = await Promise.all([
					paletteFromBlob(blob).catch(() => [] as string[]),
					parseExif(blob).catch(() => EMPTY_EXIF),
				]);
				if (isCancelled) return;

				setData((current) => ({
					...current,
					palette,
					exif: parsed.exif,
					gps: parsed.gps,
					colorSpace: parsed.colorSpace ?? current.colorSpace,
					isLimited: false,
				}));
			} else {
				const palette = await paletteFromSource(imageUrl, true).catch(
					() => [] as string[],
				);
				if (isCancelled) return;
				setData((current) => ({ ...current, palette, isLimited: true }));
			}

			if (!isCancelled) setIsLoading(false);
		};

		void run();

		return () => {
			isCancelled = true;
			controller.abort();
		};
	}, [imageUrl, filename, size, mimeType, width, height]);

	return { data, isLoading };
}
